import {
  CrmType,
  FNIType,
  LendingFlowType,
} from '@upstart/harmony/lib/constants';
import { type FormData } from './types';

type DefaultsContext = {
  oldFormData: Partial<FormData>;
  savedDealer: Pick<FormData, 'routeone'>;
};

type DefaultRule = (formData: FormData, context: DefaultsContext) => FormData;

const VINSOLUTIONS_DEFAULT_SOURCE_ID = '3541523';

const hasManualFISubmission = (fniType: FormData['fniType']) =>
  fniType === FNIType.DEALERTRACK || fniType === FNIType.ROUTEONE;

// Generic because harmony types routeone.routeOneId as required, and the
// RouteOne settings returned here may not have one yet
const withRouteOneDefaults = <T extends FormData>(
  formData: T,
  { oldFormData, savedDealer }: DefaultsContext,
): T => {
  if (
    oldFormData.fniType === formData.fniType ||
    formData.fniType !== FNIType.ROUTEONE
  ) {
    return formData;
  }
  // Don't apply the default if the form or the saved dealer already has a value
  return {
    ...formData,
    routeone: {
      ...formData.routeone,
      enableSuppressR1Application:
        formData.routeone?.enableSuppressR1Application ??
        savedDealer.routeone?.enableSuppressR1Application ??
        true,
    },
  };
};

const withVinSolutionsDefaults: DefaultRule = (formData, { oldFormData }) => {
  const wasVinSolutions = oldFormData.crmType === CrmType.VINSOLUTIONS;
  const isVinSolutions = formData.crmType === CrmType.VINSOLUTIONS;
  if (!wasVinSolutions && isVinSolutions) {
    return {
      ...formData,
      crmDefaults: {
        ...formData.crmDefaults,
        sourceId:
          formData.crmDefaults?.sourceId || VINSOLUTIONS_DEFAULT_SOURCE_ID,
      },
    };
  }
  // Clear the default source ID when changing from VinSolutions to another CRM
  if (
    wasVinSolutions &&
    !isVinSolutions &&
    formData.crmDefaults?.sourceId === VINSOLUTIONS_DEFAULT_SOURCE_ID
  ) {
    return {
      ...formData,
      crmDefaults: { ...formData.crmDefaults, sourceId: null },
    };
  }
  return formData;
};

const withNewManagerDefaults: DefaultRule = (formData, { oldFormData }) => {
  const oldManagerCount = oldFormData.managers?.length ?? 0;
  if (!formData.managers || formData.managers.length <= oldManagerCount) {
    return formData;
  }
  return {
    ...formData,
    managers: formData.managers.map((manager, i) =>
      i < oldManagerCount
        ? manager
        : {
            ...manager,
            shouldSendCreditApplication:
              manager.shouldSendCreditApplication ?? true,
          },
    ),
  };
};

const withInStoreLendingDefaults: DefaultRule = (formData, { oldFormData }) => {
  if (
    oldFormData.lending?.instore?.enabled ||
    !formData.lending?.instore?.enabled
  ) {
    return formData;
  }
  return {
    ...formData,
    lending: {
      ...formData.lending,
      ...(formData.fniType === FNIType.ROUTEONE && {
        allowOptionalInvoiceOrBookValue: true,
      }),
      instore: {
        ...formData.lending.instore,
        flowType: LendingFlowType.MANAGER,
        enableAcceptOfferScreen: true,
        ...(hasManualFISubmission(formData.fniType) && {
          manualFISubmission: true,
          submitToSelectedLenders: true,
        }),
      },
    },
  };
};

const RULES: DefaultRule[] = [
  withRouteOneDefaults,
  withVinSolutionsDefaults,
  withNewManagerDefaults,
  withInStoreLendingDefaults,
];

/**
 * Fills in related settings when the user's latest change turns something on,
 * and clears the VinSolutions source ID default when another CRM replaces
 * VinSolutions. Only the change itself triggers a default, so opening an
 * existing dealer's config never alters it.
 */
export const applyUserTriggeredDefaults = (
  oldFormData: Partial<FormData>,
  formData: FormData,
  savedDealer: Pick<FormData, 'routeone'>,
): FormData =>
  RULES.reduce(
    (data, rule) => rule(data, { oldFormData, savedDealer }),
    formData,
  );
